<template>
    <div class="preview-section ppv2-text-black ppv2-h-full" :style="{ padding: normalizeSize(40, true) }">
        <div :style="{ marginBottom: normalizeSizeVertical(13, true) }">
            <div
                class="d-flex ppv2-opacity-70"
                :style="{ gap: normalizeSize(11), marginBottom: normalizeSizeVertical(20, true) }"
            >
                <Eye :size="15" />
                <span class="preview-text" :style="{ fontSize: normalizeFontSize(12) }">{{ $t('experience.live_preview') }}</span>
            </div>
            <div class="preview-progress">
                <div class="preview-progress-bar"></div>
            </div>
        </div>

        <p class="preview-text ppv2-text-black ppv2-opacity-50" :style="{ fontSize: normalizeFontSize(12) }">
            {{ $t('experience.estimated_time', { time: '~10 minutes' }) }}
        </p>

        <div v-if="step.id === 2" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <h3
                class="preview-title ppv2-text-black"
                :style="{ fontSize: normalizeFontSize(16) }"
                :class="{ 'show-play': activeField === 'name' }"
            >
                {{ normalizedExperience.name || $t('experience.step2.labelNom') }}
            </h3>

            <div
                class="ppv2-opacity-80"
                :style="{ fontSize: normalizeFontSize(14) }"
                :class="{ 'show-play': activeField === 'description' }"
                v-text="truncatedDescription"
            ></div>

            <div
                v-if="normalizedExperience.file"
                class="preview-image-wrapper"
                :style="{ marginTop: normalizeSizeVertical(20, true) }"
                :class="{ 'show-play': activeField === 'file' }"
            >
                <!-- PDF Icon -->
                <div
                    v-if="isPdfFile"
                    class="pdf-preview-wrapper ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center ppv2-p-8"
                >
                    <svg
                        width="80"
                        height="80"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        class="ppv2-text-red-500"
                    >
                        <path
                            d="M7 18H17V16H7V18ZM7 14H17V12H7V14ZM7 10H13V8H7V10ZM14 2H6C4.89 2 4 2.89 4 4V20C4 21.1 4.89 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                            fill="currentColor"
                        />
                    </svg>
                    <span class="ppv2-text-sm ppv2-mt-2 ppv2-text-black ppv2-opacity-70">PDF</span>
                </div>
                <!-- Image -->
                <img v-else :src="getImageUrl(normalizedExperience.file)" class="preview-image" />
            </div>

            <div
                v-else
                class="preview-image-wrapper"
                :style="{ marginTop: normalizeSizeVertical(20, true) }"
                :class="{ 'show-play': activeField === 'file' }"
            >
                <img
                    :src="normalizedExperience.image ? normalizedExperience.image : '/media/bo/previewStep.jpg'"
                    class="preview-image"
                />
            </div>
        </div>
        <div v-if="step.id === 3" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <!--h3 :class="{ 'show-play': activeField === 'equipements' }" class="preview-title ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                Capacité : {{ totalCapacity }}
            </h3-->
            <h3
                :class="{ 'show-play': activeField === 'installations' }"
                class="preview-title ppv2-text-black"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{ $t('experience.capacity_label', { capacity: totalCapacity }) }}
            </h3>
        </div>
        <div v-if="step.id === 4" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <h3 class="preview-title ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                {{ $t('experience.capacity_label', { capacity: totalCapacity }) }}
            </h3>

            <!--
                Billetterie : l'étape 4 porte l'étape « Disponibilités » du billet, dont la
                configuration n'a rien à voir avec les blocs de durée d'une expérience. On affiche
                le mode retenu et, en mode personnalisé, les plages saisies.
            -->
            <div v-if="ticketAvailabilityMode" class="preview-info">
                <h3 class="preview-title ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ $t(ticketAvailabilityModeKey) }}
                </h3>
                <div v-if="ticketAvailabilitySlots.length" class="buttons-row ppv2-mb-5">
                    <span
                        v-for="(slot, slotIndex) in ticketAvailabilitySlots"
                        :key="`availability-${slotIndex}`"
                        class="ppv2-text-black time-btn ppv2-border-black"
                        :style="{ fontSize: normalizeFontSize(12) }"
                    >
                        {{ slot }}
                    </span>
                </div>
                <hr />
            </div>

            <div v-else-if="availableDurations?.length" :class="{ 'show-play': activeField === 'duration' }" class="preview-info">
                <h3 class="preview-title ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                    {{ $t('experience.preview_slots') }}
                </h3>
                <div
                    v-for="(duration, index) in sortedSelectedDurations"
                    :key="`duration-${index}`"
                    class="buttons-row ppv2-mb-5"
                >
                    <span class="ppv2-text-black time-btn ppv2-border-black" :style="{ fontSize: normalizeFontSize(12) }">
                        {{ duration.value }} min
                    </span>
                    <span
                        v-for="(available, availIndex) in sortedAvailableDurations"
                        :key="`available-${index}-${availIndex}`"
                        class="ppv2-text-black time-btn ppv2-border-black"
                        :style="{ fontSize: normalizeFontSize(12) }"
                    >
                        {{ available }}
                    </span>
                </div>
                <hr />
            </div>
        </div>
        <div v-if="step.id === 5" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <!-- Bowling : mode tarifaire jamais demandé (toujours « par groupe »), donc jamais annoncé. -->
            <h3
                v-if="!isBowlingExperience"
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'reservationMode' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{ pricingModeLabel || $t('experience.step5.reservationMode.title') }}
            </h3>
            <h3
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'price' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{
                    price
                        ? $t('experience.total_amount', { price: price + $symbolCurrency })
                        : $t('experience.step5.price.Question')
                }}
            </h3>
            <h3
                v-if="pricingModeTarif"
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'tarif' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                <BaseSelect
                    :customSizeLabel="14"
                    class="ppv2-text-black"
                    v-model="pricingModeTarif"
                    :options="getTarif"
                    :searchable="false"
                    option-label="name"
                    option-value="name"
                />
            </h3>
            <h3
                v-if="data_accompte.isAccompte"
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'accompte' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{ $t('experience.acompte_to_pay', { acompte: acompteDisplay }) }}
            </h3>
        </div>
        <div v-if="step.id === 55" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <h3 class="preview-title ppv2-text-black ppv2-mb-4" :style="{ fontSize: normalizeFontSize(15), fontWeight: '600' }">
                {{ $t('experience.stepTicketOptions.subtitle') }}
            </h3>
            <div class="ppv2-flex ppv2-flex-col ppv2-gap-3">
                <div
                    v-for="(variant, vIdx) in experience.ticket_variants || experience.tickets_as_option || []"
                    :key="vIdx"
                    class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-border ppv2-border-black ppv2-border-opacity-20 ppv2-rounded-[12px]"
                >
                    <div class="ppv2-w-4 ppv2-h-4 ppv2-rounded-full ppv2-border-2 ppv2-border-black ppv2-border-opacity-40"></div>
                    <span class="poppinsMedium ppv2-text-sm ppv2-text-black">{{
                        variant.name || variant.item_prestation?.name || `Variante ${vIdx + 1}`
                    }}</span>
                </div>
            </div>
        </div>
        <div v-if="step.id === 7" class="preview-content" :style="{ marginTop: normalizeSizeVertical(40, true) }">
            <h3 class="preview-title ppv2-text-black" :style="{ fontSize: normalizeFontSize(14) }">
                {{ $t('experience.capacity_label', { capacity: totalCapacity }) }}
            </h3>
            <h3
                v-if="isTicketPossibles"
                class="preview-title ppv2-text-black"
                :class="{ 'show-play': activeField === 'tickets' }"
                :style="{ fontSize: normalizeFontSize(14) }"
            >
                {{
                    totalTickets > 1
                        ? $t('experience.number_of_tickets', { count: totalTickets })
                        : $t('experience.step7.tickets_possibles.title')
                }}
            </h3>
        </div>
        <img src="/media/bo/icons/leftWrapper.svg" class="preview-image rounded-0 ppv2-pt-6" />
    </div>
</template>

<script>
import { Eye, Play } from 'lucide-vue'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { formatMinutesOfDay } from '../../../../utils/duration'

export default {
    name: 'PreviewExperience',
    components: { Eye, Play, BaseSelect },
    props: {
        experience: { type: [Object, Array], required: true },
        activeField: { type: String, default: null },
        step: { type: Object, required: true },
    },
    data() {
        return {
            pricingModeTarif: null,
            selectedAdditionalSale: null,
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSizeVertical,
        normalizeSize,
        getImageUrl(file) {
            if (typeof file === 'string') return file
            if (file && file instanceof File) return URL.createObjectURL(file)
            return null
        },
    },
    computed: {
        normalizedExperience() {
            if (Array.isArray(this.experience)) {
                return this.experience[0] || {}
            }
            return this.experience || {}
        },
        isPdfFile() {
            const file = this.normalizedExperience?.file
            if (!file) return false
            if (typeof file === 'string') {
                console.log('string', file.toLowerCase().includes('data:application/pdf;base64'))
                return file.toLowerCase().includes('data:application/pdf;base64')
            }
            if (file instanceof File) {
                console.log('file', file.type)
                return file.type === 'application/pdf'
            }
            return false
        },
        truncatedDescription() {
            const desc = this.normalizedExperience?.description || 'Décrivez ici votre expérience...'
            // Safe: always strip HTML tags to prevent XSS in preview
            const plainText = desc.replace(/<[^>]+>/g, '')

            if (plainText.length > 500) {
                return plainText.substring(0, 500) + '...'
            }
            return plainText
        },
        selectedInstallations() {
            const installations = this.$store.getters['experience/installations']
            return installations.filter((i) => i.selected && !i.deleted)
        },
        totalCapacity() {
            return this.selectedInstallations.reduce((sum, space) => sum + Number(space.capacity || 0), 0)
        },
        selectedDuration() {
            return this.$store.getters['experience/dynamicDurationBlocks']
        },
        /**
         * Billetterie — mode de disponibilité configuré à l'étape 4 pour un billet.
         * `null` pour une expérience classique : la preview des blocs de durée reprend alors la main.
         */
        ticketAvailabilityMode() {
            const mode = this.experience?.availability_mode
            return ['all_day', 'fixed', 'custom'].includes(mode) ? mode : null
        },
        /**
         * Clé i18n du mode. Les valeurs stockées sont en snake_case (`all_day`) alors que les locales
         * déclarent `allDay` : sans cette correspondance, la preview affichait la clé brute pour le
         * mode par défaut.
         */
        ticketAvailabilityModeKey() {
            const keys = { all_day: 'allDay', fixed: 'fixed', custom: 'custom' }
            return `experience.stepAvailability.${keys[this.ticketAvailabilityMode]}.title`
        },
        /** Plages personnalisées ("09:00 → 12:00"), vides hors mode personnalisé. */
        ticketAvailabilitySlots() {
            if (this.ticketAvailabilityMode !== 'custom') return []

            const slots = this.experience?.availability_slots || this.experience?.availabilitySlots || []
            const nextDay = this.$t('experience.stepAvailability.nextDay')

            // Le nom saisi par le gestionnaire est le libellé vu côté réservation : l'afficher ici
            // permet de vérifier la configuration sans quitter l'écran.
            return slots.map((slot) => {
                const range = `${formatMinutesOfDay(slot.start_minutes, nextDay)} → ${formatMinutesOfDay(
                    slot.end_minutes,
                    nextDay
                )}`
                return slot.name ? `${slot.name} · ${range}` : range
            })
        },
        availableDurations() {
            // Aperçu : durées de la grille qu'aucune option n'utilise encore. Calcul local — le
            // sélecteur des options, lui, propose toute la grille (deux options peuvent partager une durée).
            const blocks = this.$store.getters['experience/dynamicDurationBlocks'] || []
            const used = new Set(blocks.map((block) => Number(block.value === -1 ? block.personilize : block.value)))

            return this.$store.getters['experience/getTierDurationOptions']()
                .filter((opt) => opt.value !== -1 && !used.has(Number(opt.value)))
                .map((opt) => opt.label)
        },
        sortedSelectedDurations() {
            const blocks = this.$store.getters['experience/dynamicDurationBlocks'] || []

            return [...blocks].sort((a, b) => {
                return Number(a.value) - Number(b.value)
            })
        },

        sortedAvailableDurations() {
            return [...this.availableDurations].sort((a, b) => {
                return Number(a.value) - Number(b.value)
            })
        },
        price() {
            return this.$store.getters['experience/price']
        },
        /** Même source que `Step5.hasDynamicDuration` : la case « durée dynamique » DÉCLARE un bowling. */
        isBowlingExperience() {
            return Boolean(this.$store.getters['experience/userSelections']?.dynamic_duration_enabled)
        },
        pricingModeLabel() {
            const userSelections = this.$store.getters['experience/userSelections']
            if (userSelections.price_per_person === 1) {
                return this.$t('experience.step5.tarif_mode.person.label')
            } else if (userSelections.price_per_group === 1) {
                return this.$t('experience.step5.tarif_mode.group.label')
            }
            return ''
        },
        getTarif() {
            return this.$store.getters['experience/dynamicDurationBlocks']
        },
        additional_sales() {
            return this.$store.getters['experience/additional_sales']
        },
        activeAdditionalSales() {
            return this.additional_sales.filter((s) => s.checked && !s.deleted)
        },
        totalTickets() {
            const list = this.$store.getters['experience/list_ticket_possibles']
            if (Array.isArray(list) && list.length > 0) {
                return list.length
            }
            const min = this.$store.getters['experience/min_ticket']
            const max = this.$store.getters['experience/max_ticket']
            if (min !== -1 && max !== -1) {
                return max - min + 1
            }
            return this.$t('experience.step3.dashedCapacityOptions.notDefined')
        },
        isTicketPossibles() {
            return this.$store.getters['experience/is_ticket_possibles']
        },
        data_accompte() {
            return this.$store.getters['experience/data_accompte']
        },
        acompteDisplay() {
            if (!this.data_accompte.isAccompte) return ''
            const { type, value } = this.data_accompte.acompte
            if (type === 'percentage') {
                return `${value}%`
            } else if (type === 'amount') {
                return `${value}${this.$symbolCurrency}`
            }
            return `${value}`
        },
    },
    watch: {
        getTarif: {
            handler(newOptions) {
                if (newOptions && newOptions.length > 0 && !this.pricingModeTarif) {
                    this.pricingModeTarif = newOptions[0].name
                }
            },
            immediate: true,
        },
        activeAdditionalSales: {
            handler(newOptions) {
                if (newOptions && newOptions.length > 0 && !this.selectedAdditionalSale) {
                    this.selectedAdditionalSale = newOptions[0].name
                }
            },
            immediate: true,
        },
    },
}
</script>

<style scoped>
.preview-section {
    flex: 0.6;
    background: #d9d9d933;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.dark .preview-section {
    background: #1b194c;
}

.preview-text {
    font-family: 'Poppins Light', sans-serif;
}

.preview-progress {
    flex: 1;
    height: 7px;
    background: #9b9b9b;
    border-radius: 3px;
}

.preview-progress-bar {
    width: 80%;
    height: 100%;
    background: linear-gradient(90deg, #3a329d 27%, #8241d7 100%);
    border-radius: 3px;
}

.preview-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.preview-title {
    font-family: 'Poppins Regular', sans-serif;
    font-weight: 400;
}

.preview-image-wrapper {
    width: 100%;
    height: auto;
    overflow: hidden;
    border-radius: 12px;
}

.preview-image {
    width: 100%;
    height: auto;
    border-radius: 12px;
    object-fit: cover;
}

.preview-content .show-play {
    position: relative;
}

.preview-content .show-play::before {
    content: '';
    position: absolute;
    left: -24px;
    top: 7px;
    width: 12px;
    height: 14px;
    background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='%236C69FF' viewBox='0 0 24 24'><polygon points='8,5 19,12 8,19'/></svg>")
        no-repeat center;
    background-size: contain;
    opacity: 1;
    transition:
        opacity 0.2s ease,
        transform 0.2s ease;
}

.dark .preview-content .show-play::before {
    background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='%237D2DBD' viewBox='0 0 24 24'><polygon points='8,5 19,12 8,19'/></svg>")
        no-repeat center;
}

.preview-content .show-play:hover::before,
.preview-content .show-play::before {
    opacity: 1;
    transform: translateY(-50%) scale(1.1);
}
.buttons-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap; /* ✅ important */
    max-width: 100%; /* ou supprime */
}
.time-btn {
    padding: 10px 8%;
    border-radius: 12px;
    border: 0.5px solid;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
        background-color 0.2s ease,
        border-color 0.2s ease;
}

.time-btn:hover {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>
